"use client";

import { useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLanguage } from "@/lib/useLanguage";
import type { Language } from "@/lib/translations";
import { isMarketingLocale, isSeoLandingSlug } from "@/lib/seo/site";

const LANGS = ["es", "en", "ca"] as const;

type LanguageToggleVariant = "header" | "footer-dark" | "footer-light";

type LanguageToggleProps = {
  variant?: LanguageToggleVariant;
  /** Optional hook for pages that need extra side effects (e.g. sync ?lang= on verify-claim). */
  onLanguageChange?: (lang: Language) => void;
};

const VARIANT_STYLES: Record<LanguageToggleVariant, { wrap: string; active: string; idle: string }> =
  {
    header: {
      wrap: "bg-gray-100 dark:bg-gray-800",
      active: "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm",
      idle: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200",
    },
    "footer-dark": {
      wrap: "bg-slate-800/80 border border-slate-700/50",
      active: "bg-blue-600 text-white",
      idle: "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50",
    },
    "footer-light": {
      wrap: "bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
      active: "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm",
      idle: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200",
    },
  };

export function LanguageToggle({
  variant = "header",
  onLanguageChange,
}: LanguageToggleProps) {
  const { language, setLanguage, t } = useLanguage();
  const groupLabel = t.common?.language ?? "Language";
  const pathname = usePathname() ?? "";
  const router = useRouter();
  const styles = VARIANT_STYLES[variant];
  const isFooter = variant.startsWith("footer");
  const buttonShape = isFooter ? "rounded-full" : "rounded-md";
  const wrapShape = isFooter ? "rounded-full gap-0.5" : "rounded-lg gap-1";

  const selectLanguage = useCallback(
    (lang: Language) => {
      const segments = pathname.split("/").filter(Boolean);
      const first = segments[0] ?? "";
      const slug = segments[1] ?? "";

      if (
        segments.length >= 2 &&
        isMarketingLocale(first) &&
        isSeoLandingSlug(slug)
      ) {
        const qs =
          typeof window !== "undefined" ? window.location.search : "";
        router.push(`/${lang}/${slug}${qs}`);
      }

      setLanguage(lang);
      onLanguageChange?.(lang);
    },
    [pathname, router, setLanguage, onLanguageChange]
  );

  return (
    <div
      className={`flex items-center p-1 ${wrapShape} ${styles.wrap}`}
      role="group"
      aria-label={groupLabel}
    >
      {LANGS.map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => selectLanguage(lang)}
          className={`px-3 py-1.5 text-sm font-medium transition-colors ${buttonShape} ${
            language === lang ? styles.active : styles.idle
          }`}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
